import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/apiClient";
import { queryClient } from "../lib/queryClient";

export interface OrderItem {
  id: string;
  menu_item_id: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
}

export interface Order {
  id: string;
  user_id: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  total_amount: number;
  status:
    | "pending"
    | "confirmed"
    | "preparing"
    | "ready"
    | "completed"
    | "cancelled";
  payment_method: "cash" | "card_future";
  payment_status:
    | "pending"
    | "authorized"
    | "succeeded"
    | "failed"
    | "refunded";
  notes: string | null;
  created_on: string;
  updated_on: string;
  items: OrderItem[];
  pickup_code: string | null;
  pickup_code_expires_at: string | null;
  picked_up: boolean;
}

export interface CreateOrderRequest {
  items: Array<{
    menu_item_id: string;
    quantity: number;
  }>;
  payment_method: "cash" | "card_future";
  notes?: string;
  guest_email?: string;
  guest_phone?: string;
}

export interface UpdateOrderStatusRequest {
  status: Order["status"];
}

export function useCreateOrder() {
  return useMutation({
    mutationFn: (orderData: CreateOrderRequest) =>
      apiClient<Order>("/orders", {
        method: "POST",
        body: JSON.stringify(orderData),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useOrders(status?: string) {
  return useQuery({
    queryKey: ["orders", { status }],
    queryFn: () =>
      apiClient<Order[]>("/orders", {
        params: status ? { status } : undefined,
      }),
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: ["orders", id],
    queryFn: () => apiClient<Order>(`/orders/${id}`),
    enabled: !!id,
  });
}

export function useUpdateOrderStatus() {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateOrderStatusRequest;
    }) =>
      apiClient<Order>(`/orders/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useMarkOrderPickedUp() {
  return useMutation({
    mutationFn: (id: string) =>
      apiClient<Order>(`/orders/${id}/pickup`, {
        method: "POST",
      }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["orders", id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export interface AnalyticsOrdersResponse {
  orders: Order[];
  total: number;
  limit: number;
  offset: number;
}

export interface AnalyticsParams {
  limit?: number;
  offset?: number;
  date_from?: string;
  date_to?: string;
}

export function useAnalyticsOrders(params: AnalyticsParams = {}) {
  return useQuery({
    queryKey: ["analytics-orders", params],
    queryFn: () =>
      apiClient<AnalyticsOrdersResponse>("/orders/analytics", {
        params: {
          limit: params.limit ?? 30,
          offset: params.offset ?? 0,
          ...(params.date_from && { date_from: params.date_from }),
          ...(params.date_to && { date_to: params.date_to }),
        },
      }),
  });
}
